import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

/**
 * Hook to retrieve all branches for the current tenant along with quota info.
 */
export function useBranches() {
  const APIURL = `/branches`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    branches: data?.branches || [],
    maxBranches: data?.maxBranches || 1,
    branchCount: data?.branchCount || 0,
    canAddBranch: data?.canAddBranch ?? false,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Direct async fetcher for branches list.
 */
export async function getBranches() {
  try {
    const response = await ApiClient.get(`/branches`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Direct async fetcher for single branch details.
 */
export async function getBranchDetails(id) {
  try {
    const response = await ApiClient.get(`/branches/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Create a new branch under the current tenant.
 */
export async function addBranch({ branch_name, branch_code, phone, email, address }) {
  try {
    const response = await ApiClient.post(`/branches`, {
      branch_name,
      branch_code,
      phone,
      email,
      address,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Update branch details.
 */
export async function updateBranch(id, { branch_name, branch_code, phone, email, address, is_active }) {
  try {
    const response = await ApiClient.put(`/branches/${id}`, {
      branch_name,
      branch_code,
      phone,
      email,
      address,
      is_active,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Delete a non-main branch.
 */
export async function deleteBranch(id) {
  try {
    const response = await ApiClient.delete(`/branches/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Hook to retrieve inventory stock counts specific to a branch.
 */
export function useBranchInventory(branchId, status = "") {
  const APIURL = branchId ? `/branches/${branchId}/inventory${status ? `?status=${status}` : ""}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    data: data?.data || [],
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Hook to retrieve inter-branch stock transfer history.
 */
export function useStockTransfers(status = "") {
  const APIURL = `/branches/transfers${status ? `?status=${status}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    transfers: data?.transfers || [],
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Create and dispatch a new inter-branch stock transfer.
 */
export async function createStockTransfer({ from_branch_id, to_branch_id, notes, items }) {
  try {
    const response = await ApiClient.post(`/branches/transfers`, {
      from_branch_id,
      to_branch_id,
      notes,
      items,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Receive and confirm arrival of stock at destination branch.
 */
export async function receiveStockTransfer(transferId, { notes, items } = {}) {
  try {
    const response = await ApiClient.post(`/branches/transfers/${transferId}/receive`, {
      notes,
      items,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Cancel a dispatched transfer and restore stock back to origin branch.
 */
export async function cancelStockTransfer(transferId) {
  try {
    const response = await ApiClient.post(`/branches/transfers/${transferId}/cancel`);
    return response.data;
  } catch (error) {
    throw error;
  }
}
